import React from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Container,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Enhanced data structure with icons and descriptions
const reasons = [
  {
    title: "Sustainable Approach",
    icon: "🌱",
    description:
      "Our methods focus on long-term learning outcomes rather than short-term gains.",
  },
  {
    title: "Expert Instructors",
    icon: "🎓",
    description:
      "Skillful educators from top IITs and NITs with proven teaching experience.",
  },
  {
    title: "Holistic Education",
    icon: "🧠",
    description:
      "Comprehensive curriculum for classes 6th to 10th focusing on overall development.",
  },
  {
    title: "Research-Based Model",
    icon: "📊",
    description:
      "Curriculum developed based on successful career paths of industry leaders.",
  },
];

// Styled components
const VideoBackground = styled("video")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  zIndex: 0,
}));

const GradientOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  zIndex: 0,
}));

const ReasonCard = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  background: "rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(0, 0, 0, 0.1)",
  transition: "all 0.3s ease",
  height: "100%",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.15)",
    transform: "translateY(-5px)",
    boxShadow: "0 10px 20px rgba(0, 0, 0, 0.3)",
  },
}));

const IconBox = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.75rem",
  flexShrink: 0,
  background: "linear-gradient(135deg, #4ECDC4, #2196F3)",
  boxShadow: "0 4px 8px rgba(33, 150, 243, 0.3)",
  marginRight: theme.spacing(3),
}));

const GradientText = styled(Typography)(({ theme }) => ({
  background: "black",
  color: "black",
  fontWeight: 500,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  display: "inline-block",
  fontFamily: "Aclonica",
  textAlign: "left",
}));

// const GradientDivider = styled(Box)(({ theme }) => ({
//   height: 4,
//   width: 60,
//   margin: "0 auto",
//   background: "linear-gradient(to right, #4ECDC4, #2196F3)",
//   borderRadius: theme.shape.borderRadius,
//   marginBottom: theme.spacing(4),
// }));

const GradientButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(to right, #4ECDC4, #2196F3)",
  color: theme.palette.common.white,
  borderRadius: 30,
  padding: theme.spacing(1.5, 4),
  fontSize: "1rem",
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "0 4px 14px rgba(33, 150, 243, 0.4)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: "linear-gradient(to right, #4ECDC4, #2196F3)",
    boxShadow: "0 6px 20px rgba(33, 150, 243, 0.6)",
    transform: "translateY(-2px)",
  },
}));

const WhyChooseUs = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: "100vh",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 10,
        color: "#fff",
      }}
    >
      {/* Background Video */}

      {/* Gradient Overlay */}
      <GradientOverlay />

      {/* Content */}
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "left", mb: 2 }}>
          <GradientText variant={isSmallScreen ? "h3" : "h2"} fontWeight={700}>
            Why Choose Us?
          </GradientText>
          <Typography
            variant="h6"
            color="rgba(255, 255, 255, 0.85)"
            sx={{ maxWidth: 700, mx: "auto", mt: 3 }}
          >
            Our unique approach has been crafted to bring out the best in every
            student
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {reasons.map((reason, index) => (
            <Grid item xs={12} md={6} key={index}>
              <ReasonCard>
                <IconBox>
                  <Box component="span">{reason.icon}</Box>
                </IconBox>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      mb: 1.5,
                      color: "#4ECDC4",
                    }}
                  >
                    {reason.title}
                  </Typography>
                  <Typography variant="body1" color="#000">
                    {reason.description}
                  </Typography>
                </Box>
              </ReasonCard>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ textAlign: "center", mt: 6 }}>
          <GradientButton variant="contained" size="large">
            Join Our Program
          </GradientButton>
        </Box>
      </Container>
    </Box>
  );
};

export default WhyChooseUs;
